/**
 * The live status beside Merlin's "Drafted a question to X" line in the chat
 * (proposal 2, kanban 31f2809d). The tool call only knows it filed a draft,
 * so the chat kept saying "not sent" after the ask went out; this reads
 * where the case's ask to that person stands now.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { AssistantBlock } from '../../services/agentConsole';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';

export type AskChipKey = 'draft' | 'sent' | 'answered' | 'withdrawn';

export interface AskChip {
  key: AskChipKey;
  text: string;
}

export interface AskToolLine {
  /** Stable key for the list (the tool call's). */
  key: string;
  /** Who the call drafted the question to, as Merlin named them. */
  target: string;
  chip: AskChip | null;
}

const norm = (s: string) => s.toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9@. ]+/g, ' ').replace(/\s+/g, ' ').trim();

/** Does an ask's recipient answer to the name Merlin used ("Cliff",
 *  "Cliff Platt", "cplatt@…", "Cliff Platt (Sherwin's rep)")? */
export function namesMatch(target: string, ask: Pick<DoorDataRequestRead, 'target_name' | 'target_email'>): boolean {
  const t = norm(target);
  if (!t) return false;
  const name = norm(ask.target_name);
  const email = (ask.target_email ?? '').toLowerCase();
  if (email && t === email) return true;
  if (!name) return false;
  if (t === name || t.includes(name) || name.includes(t)) return true;
  return t.split(' ')[0] === name.split(' ')[0] && t.split(' ').length === 1;
}

/** Where the asks to `target` stand: answered beats sent beats a draft. */
export function askChipFor(target: string, asks: DoorDataRequestRead[]): AskChip | null {
  const mine = asks.filter((a) => namesMatch(target, a));
  if (mine.length === 0) return null;
  if (mine.some((a) => a.status === 'answered' || a.status === 'closed')) return { key: 'answered', text: '✓ answered' };
  if (mine.some((a) => a.status === 'open')) return { key: 'sent', text: '✓ sent' };
  if (mine.some((a) => a.status === 'proposed')) return { key: 'draft', text: 'not sent yet' };
  return { key: 'withdrawn', text: 'withdrawn' };
}

function isAskTool(name: string, rawName: string): boolean {
  return name === 'request_information' || rawName.endsWith('request_information');
}

/** Every "Drafted a question to X" call in one Merlin turn, with its chip. */
export function askToolLines(blocks: AssistantBlock[] | undefined, asks: DoorDataRequestRead[]): AskToolLine[] {
  const out: AskToolLine[] = [];
  for (const block of blocks ?? []) {
    if (block.kind !== 'tool' || !isAskTool(block.tool.name, block.tool.rawName)) continue;
    const raw = block.tool.input.target ?? block.tool.input.team;
    const target = typeof raw === 'string' ? raw.trim() : '';
    if (!target) continue;
    out.push({ key: block.tool.key, target, chip: askChipFor(target, asks) });
  }
  return out;
}
